<script setup lang="ts">
import LiveStatus from '../components/LiveStatus.vue'
import { computed, onMounted, ref } from 'vue'
import PageHero from '../components/PageHero.vue'
import StatusBadge from '../components/StatusBadge.vue'
import { request } from '../core/api'
import { useVisibleRefresh } from '../core/useVisibleRefresh'
import { navigate } from '../core/router'
import { clearSession, session } from '../core/session'
import type { JsonObject } from '../core/types'
import {
  asObject,
  asObjectList,
  formatDateTime,
  humanize,
  problemText,
  text,
} from '../core/ui'

const sanctions = ref<JsonObject[]>([])
const appeals = ref<JsonObject[]>([])
const loading = ref(false)
const error = ref('')
const notice = ref('')
const selectedSanctionId = ref('')
const grounds = ref('')
const busy = ref(false)

const restricted = computed(() => session.bootstrap?.restricted === true)
const appealBySanction = computed(() => {
  const map = new Map<string, JsonObject>()
  for (const appeal of appeals.value)
    map.set(text(appeal.sanctionId, ''), appeal)
  return map
})

async function load(background = false): Promise<void> {
  if (!background) loading.value = true
  error.value = ''
  try {
    const [sanctionPayload, appealPayload] = await Promise.all([
      request<unknown>('/api/v2/trust-safety/sanctions'),
      request<unknown>('/api/v2/trust-safety/appeals'),
    ])
    sanctions.value = Array.isArray(sanctionPayload)
      ? asObjectList(sanctionPayload)
      : asObjectList(asObject(sanctionPayload).items)
    appeals.value = Array.isArray(appealPayload)
      ? asObjectList(appealPayload)
      : asObjectList(asObject(appealPayload).items)
  } catch (caught) {
    error.value = problemText(caught)
  } finally {
    if (!background) loading.value = false
  }
}

function beginAppeal(sanctionId: string): void {
  selectedSanctionId.value = sanctionId
  grounds.value = ''
  notice.value = ''
  error.value = ''
}

async function submitAppeal(): Promise<void> {
  if (!selectedSanctionId.value || grounds.value.trim().length < 10) return
  busy.value = true
  error.value = ''
  notice.value = ''
  try {
    await request(
      `/api/v2/trust-safety/sanctions/${encodeURIComponent(selectedSanctionId.value)}/appeals`,
      {
        method: 'POST',
        idempotency: true,
        body: { grounds: grounds.value.trim() },
      },
    )
    notice.value =
      'Einspruch wurde eingereicht und an Trust & Safety übergeben.'
    selectedSanctionId.value = ''
    grounds.value = ''
    await load()
  } catch (caught) {
    error.value = problemText(caught)
  } finally {
    busy.value = false
  }
}

async function logout(): Promise<void> {
  try {
    await request('/api/v2/sessions/current', { method: 'DELETE' })
  } catch {
    // Lokaler Logout bleibt auch bei einem Netzwerkfehler möglich.
  }
  clearSession()
  navigate('/login', true)
}

function canAppeal(sanction: JsonObject): boolean {
  const id = text(sanction.sanctionId, '')
  if (!id || appealBySanction.value.has(id)) return false
  return ['ACTIVE', 'EXPIRED', 'REVOKED'].includes(
    text(sanction.state, '').toUpperCase(),
  )
}

useVisibleRefresh(() => load(true), 30000)
onMounted(() => void load())
</script>

<template>
  <section class="page-stack safety-center-page">
    <PageHero
      eyebrow="Account-Sicherheit"
      title="Sicherheitscenter"
      subtitle="Sanktionen, Begründungen und Einsprüche transparent an einem Ort."
      asset-key="account.hero"
    >
      <template v-if="restricted" #actions
        ><button type="button" @click="logout">Abmelden</button></template
      >
    </PageHero>

    <p v-if="restricted" class="warning-banner">
      Dein Account ist derzeit eingeschränkt. In diesem Modus stehen
      ausschließlich Sicherheitsinformationen und Einsprüche zur Verfügung.
    </p>
    <p v-if="notice" class="success-banner">{{ notice }}</p>
    <p v-if="error" class="error-banner">{{ error }}</p>

    <section class="panel">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Maßnahmen</p>
          <h2>Meine Sanktionen</h2>
        </div>
        <LiveStatus />
      </div>
      <div v-if="sanctions.length" class="safety-sanction-list">
        <article
          v-for="sanction in sanctions"
          :key="text(sanction.sanctionId)"
          class="safety-sanction-card"
        >
          <header>
            <div>
              <strong>{{ humanize(text(sanction.type, 'SANCTION')) }}</strong>
              <small>{{ text(sanction.reasonCode, '—') }}</small>
            </div>
            <StatusBadge :state="text(sanction.state, 'ACTIVE')" />
          </header>
          <p>
            {{
              text(sanction.rationale, 'Keine weitere Begründung hinterlegt.')
            }}
          </p>
          <dl class="fact-grid compact">
            <div>
              <dt>Beginn</dt>
              <dd>{{ formatDateTime(sanction.startsAt) }}</dd>
            </div>
            <div>
              <dt>Ende</dt>
              <dd>
                {{
                  sanction.endsAt
                    ? formatDateTime(sanction.endsAt)
                    : 'Unbefristet'
                }}
              </dd>
            </div>
            <div>
              <dt>Bereich</dt>
              <dd>
                {{
                  Array.isArray(sanction.scopes)
                    ? sanction.scopes.join(', ')
                    : '—'
                }}
              </dd>
            </div>
            <div>
              <dt>Sanktions-ID</dt>
              <dd>
                <code>{{ text(sanction.sanctionId) }}</code>
              </dd>
            </div>
          </dl>
          <div
            v-if="appealBySanction.get(text(sanction.sanctionId, ''))"
            class="appeal-summary"
          >
            <span>Einspruch</span>
            <StatusBadge
              :state="
                text(
                  appealBySanction.get(text(sanction.sanctionId, ''))?.state,
                  'OPEN',
                )
              "
            />
          </div>
          <button
            v-else-if="canAppeal(sanction)"
            type="button"
            @click="beginAppeal(text(sanction.sanctionId, ''))"
          >
            Einspruch einlegen
          </button>
        </article>
      </div>
      <p v-else-if="!loading" class="empty-state">
        Für deinen Account liegen keine Sanktionen vor.
      </p>
    </section>

    <section v-if="selectedSanctionId" class="panel appeal-compose-card">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Einspruch</p>
          <h2>Entscheidung prüfen lassen</h2>
        </div>
        <button type="button" @click="selectedSanctionId = ''">
          Schließen
        </button>
      </div>
      <p>
        Beschreibe konkret, warum die Sanktion überprüft werden soll. Der
        ursprüngliche Fall und seine Evidence bleiben unverändert.
      </p>
      <label>
        <span>Begründung</span>
        <textarea
          v-model="grounds"
          rows="6"
          maxlength="10000"
          placeholder="Mindestens 10 Zeichen …"
        />
      </label>
      <button
        class="primary"
        type="button"
        :disabled="busy || grounds.trim().length < 10"
        @click="submitAppeal"
      >
        {{ busy ? 'Wird eingereicht …' : 'Einspruch verbindlich einreichen' }}
      </button>
    </section>

    <section class="panel">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Verlauf</p>
          <h2>Meine Einsprüche</h2>
        </div>
      </div>
      <div class="table-wrap">
        <table v-if="appeals.length">
          <thead>
            <tr>
              <th>Status</th>
              <th>Sanktion</th>
              <th>Eingereicht</th>
              <th>Entscheidung</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="appeal in appeals" :key="text(appeal.appealId)">
              <td><StatusBadge :state="text(appeal.state, 'OPEN')" /></td>
              <td>
                {{ humanize(text(appeal.sanctionType, 'SANCTION'))
                }}<br /><small
                  ><code>{{ text(appeal.sanctionId) }}</code></small
                >
              </td>
              <td>{{ formatDateTime(appeal.createdAt) }}</td>
              <td>{{ text(appeal.resolution, 'Noch offen') }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="empty-state">Noch keine Einsprüche vorhanden.</p>
      </div>
    </section>
  </section>
</template>
